import { useState, useEffect } from "react";

export interface GoogleReview {
  author_name: string;
  author_url: string;
  profile_photo_url: string;
  rating: number;
  relative_time_description: string;
  text: string;
  time: number;
}

export interface GoogleReviewsData {
  reviews: GoogleReview[];
  rating: number;
  totalRatings: number;
  name: string;
}

interface State {
  data: GoogleReviewsData | null;
  loading: boolean;
  error: string | null;
}

const SCRIPT_ID = "google-maps-places-script";

function loadMapsScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).google?.maps?.places) {
      resolve();
      return;
    }
    if (document.getElementById(SCRIPT_ID)) {
      // Script tag exists but not yet loaded — wait for it
      const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement;
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Maps script failed to load")));
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Maps script failed to load"));
    document.head.appendChild(script);
  });
}

export function useGoogleReviews(placeId: string): State {
  const [state, setState] = useState<State>({ data: null, loading: true, error: null });

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
    if (!apiKey) {
      setState({ data: null, loading: false, error: "REACT_APP_GOOGLE_PLACES_API_KEY not set" });
      return;
    }
    if (!placeId) {
      setState({ data: null, loading: false, error: "No Place ID provided" });
      return;
    }

    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) setState({ data: null, loading: false, error: "Request timed out" });
    }, 10000);

    loadMapsScript(apiKey)
      .then(() => {
        const container = document.createElement("div");
        const service = new (window as any).google.maps.places.PlacesService(container);
        service.getDetails(
          {
            placeId,
            fields: ["name", "rating", "user_ratings_total", "reviews"],
          },
          (place: any, status: string) => {
            clearTimeout(timeout);
            if (cancelled) return;
            if (status !== "OK" || !place) {
              setState({ data: null, loading: false, error: `Places API error: ${status}` });
              return;
            }
            setState({
              data: {
                reviews: place.reviews || [],
                rating: place.rating || 0,
                totalRatings: place.user_ratings_total || 0,
                name: place.name || "",
              },
              loading: false,
              error: null,
            });
          }
        );
      })
      .catch((err: Error) => {
        clearTimeout(timeout);
        if (!cancelled) setState({ data: null, loading: false, error: err.message });
      });

    return () => { cancelled = true; clearTimeout(timeout); };
  }, [placeId]);

  return state;
}
